import AddWeekButton from '@UI/addWeekButton/AddWeekButton';
import AsyncStatus from '@UI/asyncStatus/AsyncStatus';
import ModalErrorBoundary from '@UI/errorBoundaries/ModalErrorBoundary';
import { useLlinkidPostMessage, useNavigatePostMessage } from '@hooks/usePostMessage';
import useSameHeight from '@hooks/useSameHeight';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import {
  selectActivitiesForSelection,
  selectAreActivitiesLoadedForSchoolyear,
  selectCalendarViewModel,
  selectIsActivityEditModalOpen,
  selectIsActivityPlanReadOnly,
  selectIsDuplicateActivityModalOpen,
} from '@store/calendar/calendarSelectors';
import {
  duplicateActivityModalClose,
  editActivityModalClose,
  editActivityModalOpen,
  moveWeek,
  setScrollToDate,
  setSelectionOptions,
} from '@store/calendar/calendarState';
import store from '@store/index';
import { selectPendingBatches } from '@store/llinkidApis/llinkidApiSelectors';
import { endLog } from '@utils/loggerHelper';
import printCalendar from '@utils/print';
import { getUnixTime } from 'date-fns/getUnixTime';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { CalendarWeek } from '@store/calendar/calendarTypes';

import { trackMoveActivity, trackSelectorUsage } from '@store/tracking/trackingSagas';
import {
  POST_MESSAGE_ACTION,
  POST_MESSAGE_EVENT,
  POST_MESSAGE_STATE,
} from '../../constants/postMessageEvents';
import CalendarHeader from './header/CalendarHeader';
import EditActivityModal from './modals/EditActivityModal';
import DuplicateActivityModal from './modals/DuplicateActivityModal';
import CalendarPrintView from './print/CalendarPrintView';
import Week from './week/Week';
import WeekSkeleton from './week/weekSkeleton/WeekSkeleton';
import { useTypedSelector } from '@hooks/typedHooks';

import './calendar.scss';

type PrintCalendarModel = {
  weeks: CalendarWeek[];
  header: { title: string };
  settings: { weekTitle: string };
};

type PrintDraftModel = Array<{ title: string; [key: string]: unknown }>;

type PrintState = {
  model: PrintCalendarModel | PrintDraftModel;
  view: 'regular' | 'other';
};

const Calendar = () => {
  const [print, setPrint] = useState<PrintState | null>(null);
  const calendar = useTypedSelector(selectCalendarViewModel);
  const areActivitiesLoaded = useTypedSelector(selectAreActivitiesLoadedForSchoolyear);
  const isSelection = useTypedSelector((state) => state.calendar.selectionMode);
  const readOnly = useTypedSelector(selectIsActivityPlanReadOnly);
  const pendingBatches = useTypedSelector(selectPendingBatches);
  const { selectionType } = useTypedSelector((state) => state.calendar.selectionOptions);
  const isActivityEditModalOpen = useTypedSelector(selectIsActivityEditModalOpen);
  const isDuplicateModalOpen = useTypedSelector(selectIsDuplicateActivityModalOpen);
  const scrollToDate = useTypedSelector((state) => state.calendar.scrollToDate);
  const ActivityModalRef = useRef();
  const dispatch = useDispatch();
  const selectionClassName = `${isSelection ? 'selection' : ''} ${
    selectionType === 'mini' ? 'selection-mini' : ''
  }`;
  const [sendMessage] = useLlinkidPostMessage({
    [POST_MESSAGE_ACTION.SELECT_YEAR_PLAN_ACTIVITIES]: (message) => {
      dispatch(setSelectionOptions(message));
    },
  });

  useNavigatePostMessage();

  const pendingBatchesRef = useRef(pendingBatches);

  useEffect(() => {
    pendingBatchesRef.current = pendingBatches;
  }, [pendingBatches]);

  useEffect(() => {
    window.onbeforeunload = (event) => {
      console.log(event);
      if (pendingBatchesRef.current?.length) {
        const e = event || window.event;
        if (e)
          e.returnValue = 'Uw aanpassingen zijn nog niet bewaard. Wilt u toch de pagina verlaten?';
        return 'Uw aanpassingen zijn nog niet bewaard. Wilt u toch de pagina verlaten?';
      }

      return null;
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  useEffect(() => {
    if (print === null) {
      const $today = document.getElementById('today');
      $today?.scrollIntoView({ block: 'center' });
    } else {
      const title =
        print.view === 'regular'
          ? (print.model as PrintCalendarModel).header.title
          : (print.model as PrintDraftModel)[0].title;
      printCalendar(title, () => setPrint(null));
    }
  }, [print?.model, print]);

  useEffect(() => {
    if (calendar) {
      endLog('open yearplan');
    }
  }, [calendar]);

  useEffect(() => {
    if (scrollToDate) {
      const $target = document.getElementById('scroll-target');
      $target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      dispatch(setScrollToDate(null));
    }
  }, [scrollToDate, dispatch]);

  const forceSetSameHeight = useSameHeight([calendar]);

  const onToggleClass = () => {
    forceSetSameHeight();
  };

  const onPrint = (model: PrintCalendarModel | PrintDraftModel, view: 'regular' | 'other') => {
    setPrint({ model, view });
    forceSetSameHeight();
  };

  const onAddWeek = (week: CalendarWeek) => {
    dispatch(trackMoveActivity());
    dispatch(
      moveWeek({
        startDate: getUnixTime(week.dateRange.start),
        type: 'ADD',
      }),
    );
  };

  const onSubtractWeek = (week: CalendarWeek) => {
    dispatch(trackMoveActivity());
    dispatch(
      moveWeek({
        startDate: getUnixTime(week.dateRange.start),
        type: 'SUBTRACT',
      }),
    );
  };

  const onAddActivityHandler = ({
    plan,
    week,
  }: {
    plan: { classes: string[] };
    week: CalendarWeek;
  }) => {
    dispatch(
      editActivityModalOpen({
        week: {
          startDate: week.startDate.toISOString(),
          endDate: week.endDate.toISOString(),
        },
        isCreate: true,
        createForClasses: plan.classes,
      }),
    );
  };

  const onModifyActivityHandler = ({
    activity,
    plan,
  }: {
    activity: { id: string; parentActivities: string[] };
    plan: { classes: string[] };
  }) => {
    dispatch(
      editActivityModalOpen({
        parentHrefs: activity.parentActivities,
        isCreate: false,
        createForClasses: plan.classes,
        key: activity.id,
      }),
    );
  };

  const onSelectActivityHandler = (activity: {
    activities_del: Array<{ $$meta: { permalink: string } }>;
  }) => {
    const state = store.getState();
    const children = activity.activities_del;

    const selection = selectActivitiesForSelection(state, {
      childrenKeys: children.map((act) => act.$$meta.permalink),
    });

    if (selection.length > 0) {
      sendMessage({
        state: POST_MESSAGE_STATE.CORRECT,
        event: POST_MESSAGE_EVENT.SELECTION_DONE,
        selection,
      });
      dispatch(trackSelectorUsage({ type: 'activities' }));
    }

    return selection;
  };

  return (
    <>
      <CalendarHeader onPrint={onPrint} onToggleClass={onToggleClass} />
      <AsyncStatus />

      {print === null && (
        <main className={`calendar ${selectionClassName}`}>
          {areActivitiesLoaded ? (
            calendar.weeks.map((week) => (
              <div key={week.id} className="week-container">
                {!isSelection && !readOnly && <AddWeekButton onMoveWeek={() => onAddWeek(week)} />}

                <Week
                  week={week}
                  scrollToDate={scrollToDate}
                  display={calendar.settings.weekTitle}
                  onMoveWeek={onSubtractWeek}
                  onAddActivity={onAddActivityHandler}
                  onModifyActivity={onModifyActivityHandler}
                  onSelectActivity={onSelectActivityHandler}
                />
              </div>
            ))
          ) : (
            <WeekSkeleton />
          )}
        </main>
      )}

      {print?.model && <CalendarPrintView model={print.model} view={print.view} />}

      <Modal
        ref={ActivityModalRef}
        size="lg"
        open={isActivityEditModalOpen}
        component={<EditActivityModal />}
        onConfirm={() => {}}
        onClose={() => {
          dispatch(editActivityModalClose());
        }}
        errorBoundaryComponent={ModalErrorBoundary}
      />

      <Modal
        size="sm"
        open={isDuplicateModalOpen}
        component={<DuplicateActivityModal />}
        onConfirm={() => {}}
        onClose={() => {
          dispatch(duplicateActivityModalClose());
        }}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </>
  );
};

export default Calendar;
